import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { SwitcherDispute } from "./SwitcherDispute";
import { Divider, Form } from "antd";
import styles from "./index.module.css";
import { useForm } from "antd/es/form/Form";
import warning from "assets/svg/Warning.svg";
import clockCountDownGray from "assets/svg/ClockCountdownGray.svg";
import infoBold from "assets/svg/infoBold.svg";
import { ModalComponent } from "Components/ModalComponent";
import {
  DebateDetailsType,
  DebateProcessingActionType,
  DebateSaveAction,
  FinishDebateRequestDto,
  RentPaymentTyp,
  ReturnDisputeType,
} from "helpers/types";
import { DateChooseComponent } from "./DateChooseComponent";
import dayjs, { Dayjs } from "dayjs";
import { FromToType } from "./DateChooseComponent/DateChooseComponent";
import { useRequest } from "ahooks";
import {
  cancelRentOfDebate,
  checkReturnTransferDate,
  debateProcessingAction,
  debateSaveAction,
  finishDebateProcess,
  getDebateReport,
  getDebateReportDocument,
  hasDebateReport,
  hasDebateReportData,
  postFileDebate,
  type GetDebateReportResponse,
} from "helpers/backend_helper";
import { AdminContext } from "context/adminContext";
import { NotificationContext } from "context/notificationContext";
import { UploadFile } from "antd/es/upload";
import { CustomSwitcher } from "Components/CustomSwitcher";
import { ModalReport } from "./ModalReport";
import { CompleteDisputeModal } from "./CompleteDisputeModal";
import { CustomTextAreaV2 } from "Components/CustomTextAreaV2";
import { CustomInputNumber } from "Components/CustomInputNumber";
import { CustomButton } from "Components/CustomButton";
import { PaperClipIcon } from "Common/Icons/PaperClip";
import { ModalComponentV1 } from "Components/ModalComponentV1";
import successIcon from "assets/svg/successIcon.svg";
import warnIcon from "assets/svg/Warning.svg";
import infoBoldIcon from "assets/svg/infoBold.svg";

type ModalContextConfirmation = {
  type: "transfer" | "return" | "cancel" | "";
  oldDate?: Dayjs;
  nextDate?: Dayjs;
};

const getDateOnly = (date: Date): Date => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

type Props = {
  debateInfo: DebateDetailsType;
  isOpen: boolean;
  close: () => void;
  onRefreshDebate: () => void;
  isReport: boolean;
  insuranceType: RentPaymentTyp;
};

const initiateDebateInfo = (
  returnTransferDate: string,
  transferStartDate: string,
): DebateProcessingActionType & { isChanged: boolean } => ({
  isChanged: false,
  lesseeRestriction: false,
  lessorRestriction: false,
  return: "ENABLE_RETURN",
  returnTransferDate,
  transferStartDate,
  stopProduct: false,
});
const RETURN_DISPUTE_TYPE: ReturnDisputeType[] = ["DISABLE_RETURN", "ENABLE_RETURN", "RETURN_IMPOSSIBLE"];

// бэкенд принимает только положительные суммы, поэтому 0 не отправляем
const positiveOrUndefined = (value: unknown) => (+value > 0 ? +value : undefined);

// та же нормализация, что и перед отправкой processing-action: конец суток по UTC
const toReturnTransferDateISO = (date: Dayjs) => {
  const endOfDate = new Date(date.toISOString());
  endOfDate.setUTCHours(23, 59, 59, 999);
  return endOfDate.toISOString();
};
export const ManageModalDisputes = ({ debateInfo, close, isOpen, onRefreshDebate, isReport, insuranceType }: Props) => {
  const [form] = useForm();
  const lesseeCompInsValue = Form.useWatch(["renter", "compIns"], form);
  const lessorCompDepValue = Form.useWatch(["lessor", "compDep"], form);
  const showLesseeIbanWarning = !debateInfo.lessee.ibanLast4 && Number(lesseeCompInsValue) > 0;
  const showLessorIbanWarning = !debateInfo.lessor.ibanLast4 && Number(lessorCompDepValue) > 0;
  const [isCancelDeal, setIsCancelDeal] = useState(false);
  const [isOpenModalAgreement, setIsOpenModalAgreement] = useState(false);
  // второй шаг той же модалки (без открытия новой) для смены даты возврата
  const [isReturnConfirmStep, setIsReturnConfirmStep] = useState(false);
  const [cancelRentsCount, setCancelRentsCount] = useState(0);
  const { localCountry } = useContext(AdminContext);
  const { openNotificationWithIcon } = useContext(NotificationContext);
  const isConfirmedRef = useRef(false);
  const keepModalOpenRef = useRef(false);
  const finishData = useRef<{ isFinish: boolean; body: FinishDebateRequestDto }>({
    isFinish: false,
    body: null,
  });
  const [filelist, setfilelist] = useState<UploadFile[]>([]);
  const saveOrResultInfo = useMemo(
    () => (isReport ? debateInfo.result : debateInfo.saveState),
    [debateInfo.result, debateInfo.saveState, isReport],
  );
  const prevProcessContext = useRef<DebateProcessingActionType & { isChanged: boolean }>(
    saveOrResultInfo
      ? { ...saveOrResultInfo, isChanged: false }
      : initiateDebateInfo(debateInfo.rent.returnTransferDate, debateInfo.rent.transferStartDate),
  );
  const [currentDates, setCurrentDates] = useState<FromToType>({
    from: dayjs(new Date(new Date(debateInfo?.rent?.transferStartDate).setUTCHours(0, 0, 0, 0))),
    to: dayjs(new Date(new Date(debateInfo?.rent?.returnTransferDate).setUTCHours(0, 0, 0, 0))),
  });
  const [processingContext, setProcessingContext] = useState<
    DebateProcessingActionType & { isChanged: boolean; type: "" | "return" | "transfer" }
  >(
    saveOrResultInfo
      ? { ...saveOrResultInfo, isChanged: false }
      : initiateDebateInfo(debateInfo.rent.returnTransferDate, debateInfo.rent.transferStartDate),
  );

  const [contextOfConfirmationModal, setContextOfConfirmationModal] = useState<ModalContextConfirmation>({
    type: "",
  });

  const { run: onCancelTheDeal } = useRequest(
    () => cancelRentOfDebate(debateInfo.rentId, localCountry.iso.toUpperCase()),
    {
      manual: true,
      ready: !!localCountry?.iso,
      refreshDeps: [localCountry?.iso],
      onError: (err: any) => {
        openNotificationWithIcon("error", err.response?.data?.message || err.message);
      },
      onSuccess: () => {
        onRefreshDebate();
        close();
      },
    },
  );

  const { run: onProcessingDispute } = useRequest(
    (body: DebateProcessingActionType) => debateProcessingAction(debateInfo.id, body, localCountry.iso.toUpperCase()),
    {
      manual: true,
      ready: !!localCountry?.iso,
      refreshDeps: [localCountry?.iso],
      onError: (err: any) => {
        form.setFieldValue("date", {
          transferDate: currentDates.from,
          returnDate: currentDates.to,
        });
        setTimeout(() => {
          setProcessingContext(() => ({ ...prevProcessContext.current, isChanged: false, type: "" }));
        }, 500);
        openNotificationWithIcon("error", err.response?.data?.message || err.message);
      },
      onSuccess: (_, params) => {
        const { type } = contextOfConfirmationModal;
        setCurrentDates((prev) => ({
          from: type === "transfer" ? contextOfConfirmationModal.nextDate : prev.from,
          to: type === "return" ? contextOfConfirmationModal.nextDate : prev.to,
        }));
        prevProcessContext.current = { ...params[0], isChanged: false };
        if (type) onRefreshDebate();
        contextOfConfirmationModal.type = null;
      },
    },
  );

  const { run: saveAction, loading: isSaveActionLoading } = useRequest(
    (body: DebateSaveAction) => debateSaveAction(debateInfo.id, body, localCountry.iso.toUpperCase()),
    {
      manual: true,
      ready: !!localCountry?.iso,
      refreshDeps: [localCountry?.iso],
      onError: (err: any) => {
        form.setFieldValue("date", {
          transferDate: currentDates.from,
          returnDate: currentDates.to,
        });
        setTimeout(() => {
          setProcessingContext(() => ({ ...prevProcessContext.current, isChanged: false, type: "" }));
        }, 500);
        openNotificationWithIcon("error", err.response?.data?.message || err.message);
      },
      onSuccess: (_, params) => {
        const { type } = contextOfConfirmationModal;
        setCurrentDates((prev) => ({
          from: type === "transfer" ? contextOfConfirmationModal.nextDate : prev.from,
          to: type === "return" ? contextOfConfirmationModal.nextDate : prev.to,
        }));
        prevProcessContext.current = { ...params[0], isChanged: false };
        close();
      },
    },
  );

  const { run: postFileReport, loading: isPostFileReportLoading } = useRequest(
    (file: UploadFile) => postFileDebate(debateInfo.id, file, localCountry.iso.toUpperCase()),
    {
      manual: true,
      ready: !!localCountry?.iso,
      refreshDeps: [localCountry?.iso],
      onError: (err: any) => {
        if (finishData.current.isFinish) {
          finishData.current = {
            body: null,
            isFinish: false,
          };
        }
        openNotificationWithIcon("error", err.response?.data?.message || err.message);
      },
      onSuccess: () => {
        if (finishData.current.isFinish) {
          finishDebate(finishData.current.body);
          form.resetFields(["reportFile"]);
          setfilelist([]);
        }
        finishData.current = {
          body: null,
          isFinish: false,
        };
      },
    },
  );

  const { run: finishDebate, loading: isFinishDebate } = useRequest(
    (body: FinishDebateRequestDto) => finishDebateProcess(debateInfo.id, body, localCountry.iso.toUpperCase()),
    {
      manual: true,
      ready: !!localCountry?.iso,
      refreshDeps: [localCountry?.iso],
      onError: (err: any) => {
        openNotificationWithIcon("error", err.response?.data?.message || err.message);
      },
      onSuccess: () => {
        if (finishData.current.isFinish) {
          finishDebate(finishData.current.body);
          form.resetFields(["reportFile"]);
          setfilelist([]);
        }
        finishData.current = {
          body: null,
          isFinish: false,
        };
        setCompleteModalValues(null);
        onRefreshDebate();
        close();
      },
    },
  );

  // значения формы, показываемые в модалке подтверждения; null — модалка закрыта
  const [completeModalValues, setCompleteModalValues] = useState<any>(null);
  const pendingFinishBody = useRef<FinishDebateRequestDto>(null);

  const { run: confirmCompleteDispute, loading: isConfirmCompleteLoading } = useRequest(
    async (blockAd: boolean) => {
      if (blockAd !== !!processingContext.stopProduct) {
        await debateProcessingAction(
          debateInfo.id,
          {
            lesseeRestriction: !!processingContext.lesseeRestriction,
            lessorRestriction: !!processingContext.lessorRestriction,
            return: processingContext.return,
            stopProduct: blockAd,
          },
          localCountry.iso.toUpperCase(),
        );
      }
    },
    {
      manual: true,
      ready: !!localCountry?.iso,
      onError: (err: any) => {
        openNotificationWithIcon("error", err.response?.data?.message || err.message);
      },
      onSuccess: (_, [blockAd]) => {
        setProcessingContext((prev) => ({ ...prev, stopProduct: blockAd }));
        prevProcessContext.current = { ...prevProcessContext.current, stopProduct: blockAd };
        const body = pendingFinishBody.current;
        if (filelist[0]) {
          finishData.current = { body, isFinish: true };
          postFileReport(filelist[0]);
        } else {
          finishDebate(body);
        }
      },
    },
  );

  const [isOpenModalReport, setIsOpenModalReport] = useState(false);
  const [report, setReport] = useState<GetDebateReportResponse | null>(null);

  const hasReport = hasDebateReport(report);
  const hasReportData = hasDebateReportData(report);
  const [showReportWarning, setShowReportWarning] = useState(false);

  useEffect(() => {
    if (hasReport) setShowReportWarning(false);
  }, [hasReport]);

  const onFinish = useCallback(
    (fields) => {
      finishData.current = {
        body: null,
        isFinish: false,
      };
      if (isReport) return;

      if (!hasReport) {
        setShowReportWarning(true);
        return;
      }

      const result: FinishDebateRequestDto = {
        lesseeResponsibility: !!fields.renter?.responsible,
        lesseeDebt: positiveOrUndefined(fields.renter?.debt),
        decision: fields?.publicComment ? fields?.publicComment : "",
        decisionNotes: fields?.internalComment ? fields.internalComment : "",
        customInsuranceCompensation: {
          lesseeAmount: positiveOrUndefined(fields.renter?.compIns),
          lessorAmount: insuranceType === "PERCENTAGE" ? positiveOrUndefined(fields.lessor?.compDep) : null,
        },
        rentLessorPayoutFromDeposit:
          insuranceType !== "PERCENTAGE" ? positiveOrUndefined(fields.lessor?.compDep) : null,
        lessorDebt: positiveOrUndefined(fields.lessor?.debt),
        // customRentalPaymentDistribution: {
        //   lesseeAmount: fields.renter?.rentalReturn ? +fields.renter?.rentalReturn : 0,
        //   lessorAmount: +debateInfo?.rent?.rentPrice - (fields.renter?.rentalReturn ? +fields.renter?.rentalReturn : 0),
        // },
      };

      // if (insuranceType === "DEPOSIT") {
      //   result.customDepositDistribution = {
      //     lesseeAmount: debateInfo?.rent?.product?.price - (fields.lessor?.compDep ? +fields.lessor.compDep : 0),
      //     lessorAmount: fields.lessor?.compDep ? +fields.lessor.compDep : 0,
      //   };
      // }
      pendingFinishBody.current = result;
      setCompleteModalValues(form.getFieldsValue());
    },
    [form, hasReport, insuranceType, isReport],
  );

  useEffect(() => {
    if (processingContext.isChanged) {
      const result = JSON.parse(JSON.stringify(processingContext));
      delete result.isChanged;
      let returnDateISO: string = undefined;
      if (result.returnTransferDate) {
        const returnDateEndOfDate = new Date(result.returnTransferDate);
        returnDateEndOfDate.setUTCHours(23, 59, 59, 999);
        returnDateISO = returnDateEndOfDate.toISOString();
      }
      onProcessingDispute({
        lesseeRestriction: result.lesseeRestriction,
        lessorRestriction: result.lessorRestriction,
        return: result.return,
        returnTransferDate:
          processingContext.type === "return" ? (returnDateISO ? returnDateISO : undefined) : undefined,
        returnWithDispute: processingContext.type === "return" ? result.returnWithDispute : undefined,
        stopProduct: result.stopProduct,
        transferStartDate: processingContext.type === "transfer" ? result.transferStartDate : undefined,
      });
      setProcessingContext((prev) => ({ ...prev, isChanged: false, type: "" }));
    }
  }, [debateInfo.rent.returnTransferDate, debateInfo.rent.transferStartDate, onProcessingDispute, processingContext]);

  const openConfirmDateModal = useCallback(
    (isOpen, type, newDates) => {
      setIsOpenModalAgreement(isOpen);
      setIsReturnConfirmStep(false);
      setCancelRentsCount(0);
      setContextOfConfirmationModal({
        type,
        nextDate: type === "transfer" ? newDates.from : newDates.to,
        oldDate: type === "transfer" ? currentDates.from : currentDates.to,
      });
    },
    [currentDates?.from, currentDates?.to],
  );

  const closeConfirmationModal = useCallback(() => {
    setIsCancelDeal(false);
    setIsOpenModalAgreement(false);
    setIsReturnConfirmStep(false);
    setCancelRentsCount(0);
  }, []);

  const declineConfirmationModal = useCallback(() => {
    closeConfirmationModal();
    if (contextOfConfirmationModal.type === "cancel") return;
    form.setFieldValue("date", {
      transferDate: currentDates.from,
      returnDate: currentDates.to,
    });
  }, [closeConfirmationModal, contextOfConfirmationModal.type, currentDates.from, currentDates.to, form]);

  const isNextDateInFuture = useMemo(
    () =>
      contextOfConfirmationModal?.nextDate
        ? getDateOnly(contextOfConfirmationModal.nextDate.toDate()) >= getDateOnly(new Date())
        : false,
    [contextOfConfirmationModal?.nextDate],
  );

  const applyReturnDate = useCallback(
    (returnWithDispute?: boolean) => {
      isConfirmedRef.current = true;
      setProcessingContext((prev) => ({
        ...prev,
        returnTransferDate: contextOfConfirmationModal.nextDate.toISOString(),
        returnWithDispute,
        isChanged: true,
        type: "return",
      }));
      closeConfirmationModal();
      setTimeout(() => {
        isConfirmedRef.current = false;
      });
    },
    [closeConfirmationModal, contextOfConfirmationModal?.nextDate],
  );

  const { run: checkReturnDate, loading: isCheckReturnDateLoading } = useRequest(
    (returnTransferDate: string) =>
      checkReturnTransferDate(debateInfo.id, { returnTransferDate }, localCountry.iso.toUpperCase()),
    {
      manual: true,
      ready: !!localCountry?.iso,
      refreshDeps: [localCountry?.iso],
      onError: (err: any) => {
        openNotificationWithIcon("error", err.response?.data?.message || err.message);
      },
      onSuccess: (res) => {
        if (res?.willCancelRents) {
          setCancelRentsCount(res.cancelRentsCount ?? res.cancelRents?.length ?? 0);
          setIsReturnConfirmStep(true);
        } else {
          applyReturnDate();
        }
      },
    },
  );

  const initiateValues = useMemo(() => {
    let savedState: any = {};

    if (isReport) {
      console.log(debateInfo);
      savedState = saveOrResultInfo
        ? {
            limitations: {
              lessor: processingContext.lessorRestriction,
              renter: processingContext.lesseeRestriction,
              disableAd: processingContext.stopProduct,
            },
            renter: {
              // rentalReturn: saveOrResultInfo?.customDepositDistribution?.lesseeAmount,
              compIns: saveOrResultInfo?.customInsuranceCompensation?.lesseeAmount,
              debt: saveOrResultInfo?.lesseeDebt,
              provideOffline: saveOrResultInfo?.customInsuranceCompensation?.isPaidOfflineLessee,
              responsible: saveOrResultInfo?.lesseeResponsibility,
            },
            lessor: {
              compDep:
                insuranceType === "DEPOSIT"
                  ? saveOrResultInfo?.rentLessorPayoutFromDeposit
                  : saveOrResultInfo?.customInsuranceCompensation?.lessorAmount,
              debt: saveOrResultInfo?.lessorDebt,
              provideOffline: saveOrResultInfo?.customInsuranceCompensation?.isPaidOfflineLessor,
            },
            publicComment: debateInfo?.decision,
            internalComment: saveOrResultInfo?.decisionNotes,
          }
        : {};
    } else {
      savedState = saveOrResultInfo
        ? {
            limitations: {
              lessor: processingContext.lessorRestriction,
              renter: processingContext.lesseeRestriction,
              disableAd: processingContext.stopProduct,
            },
            renter: {
              // rentalReturn: saveOrResultInfo?.customDepositDistribution?.lesseeAmount,
              compIns: saveOrResultInfo?.customInsuranceCompensation?.lesseeAmount,
              debt: saveOrResultInfo?.lesseeDebt,
              provideOffline: saveOrResultInfo?.customInsuranceCompensation?.isPaidOfflineLessee,
              responsible: saveOrResultInfo?.lesseeResponsibility,
            },
            lessor: {
              compDep:
                insuranceType === "DEPOSIT"
                  ? saveOrResultInfo?.rentLessorPayoutFromDeposit
                  : saveOrResultInfo?.customInsuranceCompensation?.lessorAmount,
              debt: saveOrResultInfo?.lessorDebt,
              provideOffline: saveOrResultInfo?.customInsuranceCompensation?.isPaidOfflineLessor,
            },
            publicComment: saveOrResultInfo?.decision,
            internalComment: saveOrResultInfo?.decisionNotes,
          }
        : {};
    }
    return {
      date: {
        transferDate: dayjs(debateInfo.rent.transferStartDate),
        returnDate: dayjs(
          new Date(debateInfo.rent.returnTransferDate).setDate(dayjs(debateInfo.rent.returnTransferDate).date() - 1),
        ),
      },
      ...savedState,
    };
  }, [
    isReport,
    debateInfo,
    saveOrResultInfo,
    processingContext.lessorRestriction,
    processingContext.lesseeRestriction,
    processingContext.stopProduct,
    insuranceType,
  ]);

  const isDisabledReturnSwitcher = useMemo(
    () =>
      debateInfo.rent.status === "PRODUCT_RETURNED" ||
      debateInfo.rent.status === "COMPLETED" ||
      dayjs(new Date(new Date(debateInfo.rent.returnTransferDate).setUTCHours(0, 0, 0, 0))).format("DD-MM-YYYY") ===
        dayjs(new Date()).format("DD-MM-YYYY"),
    [debateInfo.rent.returnTransferDate, debateInfo.rent.status],
  );

  const isShownCompensation = useMemo(() => {
    const correctDate = new Date(processingContext.returnTransferDate);
    correctDate.setHours(0, 0, 0, 0);
    return (
      isReport ||
      (processingContext.return === "ENABLE_RETURN" && debateInfo.rent.status === "PRODUCT_RETURNED") ||
      debateInfo.rent.status === "COMPLETED" ||
      processingContext.return === "RETURN_IMPOSSIBLE"
    );
  }, [debateInfo.rent.status, isReport, processingContext.return, processingContext.returnTransferDate]);
  console.log(debateInfo);

  const isReportGenerating = report?.status?.toUpperCase() === "GENERATING";

  const { refresh: refreshReport, cancel: cancelReportPolling } = useRequest(
    () => getDebateReport(debateInfo.id, localCountry.iso.toUpperCase()),
    {
      ready: !!localCountry?.iso,
      refreshDeps: [localCountry?.iso, debateInfo.id],
      pollingInterval: 10000,
      onSuccess: (res) => {
        setReport(res);
        if (res?.status?.toUpperCase() !== "GENERATING") cancelReportPolling();
      },
      onError: () => {
        setReport(null);
        cancelReportPolling();
      },
    },
  );

  const onOpenModalReport = () => {
    setIsOpenModalReport(true);
  };
  const onCloseModalReport = () => {
    setIsOpenModalReport(false);
    refreshReport();
  };

  const { run: onOpenReportDocument, loading: isOpenReportLoading } = useRequest(
    () => getDebateReportDocument(report.reportUrl, localCountry.iso.toUpperCase()),
    {
      manual: true,
      ready: !!localCountry?.iso && !!report?.reportUrl,
      onSuccess: (res) => {
        const url = URL.createObjectURL(res.data);
        window.open(url, "_blank");
      },
      onError: (err: any) => {
        openNotificationWithIcon("error", err.response?.data?.message || err.message);
      },
    },
  );

  const now = new Date();
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const returnTransferDateZeroHrs = new Date(new Date(processingContext.returnTransferDate).setUTCHours(0, 0, 0, 0));
  const isTimeToShowReturnInfo = today >= returnTransferDateZeroHrs;
  const isSameDay = today.getTime() === returnTransferDateZeroHrs.getTime();
  const shownReturnInfoType =
    isSameDay && debateInfo?.rent?.status !== "COMPLETED" && debateInfo?.rent?.status !== "PRODUCT_RETURNED"
      ? 3
      : debateInfo?.rent?.status === "COMPLETED" || debateInfo?.rent?.status === "PRODUCT_RETURNED"
      ? 1
      : 2;
  console.log(
    isSameDay,
    debateInfo?.rent?.status,
    shownReturnInfoType,
    isTimeToShowReturnInfo,
    returnTransferDateZeroHrs,
    today,
    returnTransferDateZeroHrs,
  );
  return (
    <>
      <ModalComponentV1
        title="Manage Dispute"
        className={`${isShownCompensation ? styles.modal : styles.modal + " " + styles.fiftyTwenty}`}
        isOpen={isOpen}
        onClose={close}
      >
        <Form
          layout="vertical"
          form={form}
          className={styles.cascaderWrapper}
          onFinish={onFinish}
          initialValues={initiateValues}
          requiredMark={false}
        >
          <Form.Item
            label={
              <div className={styles.limitationWrapper}>
                <img src={warning} alt="warning" />
                <span className={styles.titleFirst}>Limitations</span>
              </div>
            }
            className={styles.limitationsWrapper}
          >
            <Form.Item name={["limitations", "lessor"]} noStyle>
              <div className={styles.limitationSwitchWrapper}>
                <CustomSwitcher
                  onChange={(checked) => {
                    setProcessingContext((prev) => ({ ...prev, lessorRestriction: checked, isChanged: true }));
                  }}
                  defaultChecked={debateInfo?.saveState?.lessorRestriction}
                  disabled={isReport}
                />
                <span className={styles.switchLimitationLabel}>Lessor</span>
              </div>
            </Form.Item>
            <Form.Item name={["limitations", "renter"]} noStyle>
              <div className={styles.limitationSwitchWrapper}>
                <CustomSwitcher
                  onChange={(checked) => {
                    setProcessingContext((prev) => ({ ...prev, lesseeRestriction: checked, isChanged: true }));
                  }}
                  defaultChecked={debateInfo?.saveState?.lesseeRestriction}
                  disabled={isReport}
                />
                <span className={styles.switchLimitationLabel}>Renter</span>
              </div>
            </Form.Item>
            <Form.Item name={["limitations", "disableAd"]} noStyle>
              <div className={styles.limitationSwitchWrapper}>
                <CustomSwitcher
                  onChange={(checked) => {
                    setProcessingContext((prev) => ({ ...prev, stopProduct: checked, isChanged: true }));
                  }}
                  defaultChecked={debateInfo?.saveState?.stopProduct}
                  disabled={isReport}
                />
                <span className={styles.switchLimitationLabel}>Block Ad</span>
              </div>
            </Form.Item>
          </Form.Item>
          <Divider style={{ borderWidth: 2, borderColor: "#C6C5C5", margin: "20px 0" }} />

          {!(
            debateInfo.rent.status === "WAITING_FOR_LESSEE_ACCEPT" ||
            debateInfo.rent.status === "ACCEPTED" ||
            debateInfo.rent.status === "BOOKED"
          ) ? (
            <div className={styles.returnSwithcer}>
              {processingContext.return === "DISABLE_RETURN" ? (
                <div className={styles.returnSwithcer}>
                  <div className={styles.titleFirst} style={{ marginBottom: 20 }}>
                    Transfer and Return
                  </div>
                </div>
              ) : (
                <div className={styles.titleFirst} style={{ marginBottom: 20 }}>
                  Return
                </div>
              )}
              <SwitcherDispute
                isDisabled={isDisabledReturnSwitcher || isReport}
                onChoose={(v) => {
                  if (
                    RETURN_DISPUTE_TYPE[v] ===
                    RETURN_DISPUTE_TYPE[RETURN_DISPUTE_TYPE.findIndex((v) => v === processingContext.return)]
                  )
                    return;
                  setProcessingContext((prev) => ({ ...prev, return: RETURN_DISPUTE_TYPE[v], isChanged: true }));
                }}
                value={
                  isDisabledReturnSwitcher
                    ? debateInfo?.saveState?.return === "RETURN_IMPOSSIBLE"
                      ? 2
                      : 1
                    : RETURN_DISPUTE_TYPE.findIndex((v) => v === processingContext.return)
                }
              />
            </div>
          ) : (
            <div className={styles.returnSwithcer}>
              <div className={styles.titleFirst} style={{ marginBottom: 20 }}>
                Transfer and Return
              </div>
            </div>
          )}

          <DateChooseComponent
            dateFrom={debateInfo.rent.dateStart}
            isCompenstation={
              isShownCompensation ? (isTimeToShowReturnInfo ? true : false) : isTimeToShowReturnInfo ? true : false
            }
            blocked={{
              isReturnDateBlocked:
                processingContext.return !== "ENABLE_RETURN" ||
                isReport ||
                debateInfo.rent.status === "PRODUCT_RETURNED" ||
                debateInfo.rent.status === "COMPLETED" ||
                debateInfo.rent.status === "CANCELLED" ||
                debateInfo.rent.status === "ACCEPTED" ||
                debateInfo.rent.status === "BOOKED" ||
                debateInfo.rent.status === "PENDING_APPROVAL",
              isTransferBlocked:
                !(
                  debateInfo.rent.status === "ACCEPTED" ||
                  debateInfo.rent.status === "BOOKED" ||
                  isDisabledReturnSwitcher
                ) ||
                isReport ||
                debateInfo.rent.status === "PRODUCT_RETURNED" ||
                debateInfo.rent.status === "COMPLETED" ||
                debateInfo.rent.status === "CANCELLED" ||
                debateInfo.rent.status === "WAITING_FOR_LESSOR_ACCEPT" ||
                debateInfo.rent.status === "STARTED",
            }}
            openConfirmDateModal={openConfirmDateModal}
            valueDates={currentDates}
            returnAct={
              (debateInfo.rent.status === "PRODUCT_RETURNED" || debateInfo.rent.status === "COMPLETED") &&
              (() => {
                const date = currentDates.to.toDate();
                date.setDate(date.getDate());
                return dayjs(date).format("DD.MM.YY");
              })()
            }
          />
          {isTimeToShowReturnInfo && (
            <>
              {shownReturnInfoType === 1 && (
                <div className={styles.prodReturnedStatus} style={{ backgroundColor: "#D8EDD0" }}>
                  <img src={successIcon} alt="success icon" width={15} />
                  <span>Item has been Returned</span>
                </div>
              )}
              {shownReturnInfoType === 2 && (
                <div className={styles.prodReturnedStatus} style={{ backgroundColor: "#FADBDB" }}>
                  <img src={warnIcon} alt="success icon" />
                  <span>Item hasn´t been Returned by date</span>
                </div>
              )}

              {shownReturnInfoType === 3 && (
                <div className={styles.prodReturnedStatus} style={{ backgroundColor: "#E7E7E7" }}>
                  <img src={infoBoldIcon} alt="success icon" />
                  <span>The Return is scheduled for today</span>
                </div>
              )}
            </>
          )}

          {(debateInfo.rent.status === "WAITING_FOR_LESSEE_ACCEPT" ||
            debateInfo.rent.status === "ACCEPTED" ||
            debateInfo.rent.status === "BOOKED") &&
            !isReport && (
              <div className={styles.warningReturn}>
                <div style={{ display: "flex", gap: 4 }}>
                  <img src={infoBold} alt="warning" />
                  <span>Deal paid, but Item hasn’t been transferred</span>
                </div>
                <CustomButton
                  typeButton="critical"
                  sizeButton="medium"
                  onClick={() => {
                    setIsCancelDeal(true);
                    setIsOpenModalAgreement(true);
                  }}
                >
                  Cancel the Deal
                </CustomButton>
              </div>
            )}

          {isShownCompensation && (
            <>
              <Divider style={{ borderWidth: 2, borderColor: "#C6C5C5", margin: "20px 0" }} />
              <div className={styles.titleFirst} style={{ marginBottom: 20 }}>
                Compensation
              </div>
              <Form.Item className={styles.compensationWrapper} style={{ marginBottom: 20 }}>
                <Form.Item style={{ width: "100%" }}>
                  <Form.Item className={styles.rentcompwrap} style={{ marginBottom: 16 }}>
                    <span className={styles.titleFirst}>Renter</span>
                    <Form.Item name={["renter", "responsible"]} style={{ display: "inline-block" }}>
                      <div className={styles.limitationSwitchWrapper}>
                        <CustomSwitcher
                          onChange={(checked) => {
                            const currentFieldsOfLessor = form.getFieldValue("renter");
                            form.setFieldValue("renter", { ...currentFieldsOfLessor, responsible: checked });
                          }}
                          defaultChecked={saveOrResultInfo?.lesseeResponsibility}
                          disabled={isReport}
                        />
                        <span className={styles.switchLimitationLabel}>Responsible</span>
                      </div>
                    </Form.Item>
                  </Form.Item>

                  <Form.Item
                    label="Compensation from Insurance"
                    name={["renter", "compIns"]}
                    className={styles.labelFormFont}
                    style={{ marginBottom: 4 }}
                    getValueFromEvent={(num) => {
                      return num ?? null;
                    }}
                    rules={[
                      {
                        required: true,
                        type: "number",
                        min: 0,
                        max: debateInfo?.rent?.product?.price,
                        message: "wrong amount",
                      },
                    ]}
                  >
                    <CustomInputNumber
                      suffix="€"
                      placeholder={debateInfo.rent.product.price + " max"}
                      disabled={isReport}
                    />
                  </Form.Item>
                  {showLesseeIbanWarning ? (
                    <div className={styles.attachWarnign} style={{ margin: 0, marginBottom: 8 }}>
                      <img src={warning} alt="warning" />
                      <span>IBAN not connected</span>
                    </div>
                  ) : null}
                  <Form.Item
                    label="Debt"
                    name={["renter", "debt"]}
                    className={styles.labelFormFont}
                    getValueFromEvent={(num) => {
                      return num ?? null;
                    }}
                    rules={[
                      {
                        required: true,
                        type: "number",
                        min: 0,
                        max: debateInfo?.rent?.product?.price,
                        message: "wrong amount",
                      },
                    ]}
                  >
                    <CustomInputNumber
                      suffix="€"
                      placeholder={debateInfo.rent.product.price + " max"}
                      inputMode="decimal"
                      disabled={isReport}
                    />
                  </Form.Item>
                </Form.Item>
                <Form.Item style={{ width: "100%" }}>
                  <div style={{ height: 24, display: "flex", alignItems: "center", marginBottom: 16 }}>
                    <span className={styles.titleFirst}>Lessor</span>
                  </div>
                  <Form.Item
                    label={`Compensation from ${insuranceType === "DEPOSIT" ? "Deposit" : "Insurance"}`}
                    name={["lessor", "compDep"]}
                    className={styles.labelFormFont}
                    style={{ marginBottom: 4 }}
                    getValueFromEvent={(num) => {
                      return num ?? null;
                    }}
                    rules={[
                      insuranceType === "DEPOSIT"
                        ? {
                            required: true,
                            max: debateInfo?.rent?.product?.price,
                            type: "number",
                            min: 0,
                            message: "wrong amount",
                          }
                        : {
                            required: true,
                            max: debateInfo?.rent?.product?.price,
                            type: "number",
                            min: 0,
                            message: "wrong amount",
                          },
                    ]}
                  >
                    <CustomInputNumber
                      suffix="€"
                      placeholder={debateInfo.rent.product.price + " max"}
                      inputMode="decimal"
                      disabled={isReport}
                    />
                  </Form.Item>
                  {showLessorIbanWarning ? (
                    <div className={styles.attachWarnign} style={{ margin: 0, marginBottom: 8 }}>
                      <img src={warning} alt="warning" />
                      <span>IBAN not connected</span>
                    </div>
                  ) : null}
                  <Form.Item
                    label="Debt"
                    name={["lessor", "debt"]}
                    className={styles.labelFormFont}
                    getValueFromEvent={(num) => {
                      return num ?? null;
                    }}
                    rules={[
                      {
                        required: true,
                        type: "number",
                        min: 0,
                        max: debateInfo?.rent?.product?.price,
                        message: "wrong amount",
                      },
                    ]}
                  >
                    <CustomInputNumber
                      suffix="€"
                      placeholder={debateInfo.rent.product.price + " max"}
                      inputMode="decimal"
                      disabled={isReport}
                    />
                  </Form.Item>
                </Form.Item>
              </Form.Item>
              <Form.Item
                name="publicComment"
                label="Public comment (will be shown in common Chat)"
                className={styles.labelFormFont}
                style={{ marginBottom: 8 }}
                rules={[
                  {
                    required: true,
                    type: "string",
                    min: 0,
                  },
                ]}
              >
                <CustomTextAreaV2 style={{ minHeight: 153 }} placeholder="Public comment" disabled={isReport} />
              </Form.Item>
              {/* <Form.Item
                name="reportFile"
                valuePropName="filelist"
                rules={[
                  {
                    required: !debateInfo?.report,
                    message: "",
                  },
                ]}
              >
                <div className={styles.fileAttachWrapper}>
                  <Upload
                    beforeUpload={() => false}
                    className={filelist.length > 0 ? styles.upload : undefined}
                    disabled={isReport}
                    fileList={filelist}
                    onChange={({ fileList }) => {
                      setfilelist(fileList);
                    }}
                  >
                    <Button
                      type="primary"
                      ghost
                      icon={<PaperClipIcon width="16px" height="16px" margin="0" backgroundSize="16px 16px" />}
                      className={styles.uploadReport}
                      disabled={filelist.length > 0 || isReport}
                    >
                      Attach Report
                    </Button>
                  </Upload>
                  {debateInfo.report && (
                    <Button type="link" href={debateInfo.report} target="_blank" className={styles.reportLink}>
                      Report
                    </Button>
                  )}
                </div>
              </Form.Item> */}
              <div className={styles.fileAttachWrapper} style={{ marginBottom: hasReport ? 20 : 8 }}>
                {hasReport && (
                  <CustomButton
                    sizeButton="large"
                    typeButton="secondary"
                    loading={isOpenReportLoading}
                    onClick={() => onOpenReportDocument()}
                  >
                    Open Report
                  </CustomButton>
                )}
                {!isReport && (
                  <CustomButton
                    sizeButton="large"
                    typeButton="tertiary"
                    onClick={onOpenModalReport}
                    disabled={isReportGenerating}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <PaperClipIcon filter="brightness(0)" />
                      <span>{hasReportData ? "Change Report" : "Create Report"}</span>
                    </div>
                  </CustomButton>
                )}
              </div>
              {isReportGenerating ? (
                <div
                  className={styles.attachWarnign}
                  style={{ margin: 0, marginBottom: 20, backgroundColor: "#E7E7E7" }}
                >
                  <img src={clockCountDownGray} alt="warning" />
                  <span>The report is being generated</span>
                </div>
              ) : showReportWarning ? (
                <div className={styles.attachWarnign} style={{ margin: 0, marginBottom: 20 }}>
                  <img src={warning} alt="warning" />
                  <span>Attach Report to complete the Dispute</span>
                </div>
              ) : null}
              <Form.Item
                name="internalComment"
                label="Internal comment (will be visible only for Admins)"
                className={styles.labelFormFont}
                style={{ marginBottom: 20 }}
                rules={[
                  {
                    required: true,
                    type: "string",
                    min: 0,
                  },
                ]}
              >
                <CustomTextAreaV2 style={{ minHeight: 153 }} placeholder="Internal Comment" disabled={isReport} />
              </Form.Item>
            </>
          )}
          <div className={styles.actionBtnsWrapper}>
            <CustomButton
              disabled={isReport}
              typeButton={isShownCompensation ? "secondary" : "primary"}
              sizeButton="large"
              onClick={() => {
                if (processingContext.return === "DISABLE_RETURN") {
                  close();
                  return;
                }
                const fields = form.getFieldsValue();
                saveAction({
                  return: processingContext.return,
                  stopProduct: !!processingContext.stopProduct,
                  lessorRestriction: !!processingContext.lessorRestriction,
                  lesseeRestriction: !!processingContext.lesseeRestriction,
                  returnTransferDate: processingContext.returnTransferDate,
                  transferStartDate: processingContext.transferStartDate,
                  lesseeResponsibility: !!fields.renter?.responsible,
                  lessorDebt: positiveOrUndefined(fields.lessor?.debt),
                  lesseeDebt: positiveOrUndefined(fields.renter?.debt),
                  decision: fields?.publicComment ? fields?.publicComment : "",
                  decisionNotes: fields?.internalComment ? fields.internalComment : "",
                  // customDepositDistribution: {
                  //   lesseeAmount:
                  //     debateInfo?.rent?.product?.price - (fields.lessor?.compDep ? +fields.lessor.compDep : 0),
                  //   lessorAmount: fields.lessor?.compDep ? +fields.lessor.compDep : 0,
                  // },
                  customInsuranceCompensation: {
                    lesseeAmount: positiveOrUndefined(fields.renter?.compIns),
                    lessorAmount: insuranceType === "PERCENTAGE" ? positiveOrUndefined(fields.lessor?.compDep) : null,
                  },
                  rentLessorPayoutFromDeposit:
                    insuranceType !== "PERCENTAGE" ? positiveOrUndefined(fields.lessor?.compDep) : null,
                });
                if (filelist.length) postFileReport(filelist[0]);
              }}
              loading={isSaveActionLoading || isPostFileReportLoading}
            >
              {!isShownCompensation ? "Close" : "Save Draft & Close"}
            </CustomButton>
            {isShownCompensation && (
              <CustomButton
                disabled={isReport || isReportGenerating}
                htmlType="submit"
                loading={isFinishDebate}
                sizeButton="large"
                typeButton="primary"
              >
                Complete Dispute
              </CustomButton>
            )}
          </div>
        </Form>
      </ModalComponentV1>
      <ModalComponent
        buttons={{
          confirm: {
            label: isCancelDeal ? "Cancel Deal" : "Confirm",
            onConfirm: async () => {
              const { type } = contextOfConfirmationModal;
              if (!isCancelDeal && type !== "transfer") {
                // дата возврата подтверждается на втором шаге этой же модалки
                keepModalOpenRef.current = true;
                if (isNextDateInFuture) {
                  // для будущей даты сначала спрашиваем бэк, будут ли отменены аренды
                  checkReturnDate(toReturnTransferDateISO(contextOfConfirmationModal.nextDate));
                } else {
                  setIsReturnConfirmStep(true);
                }
                return;
              }
              isConfirmedRef.current = true;
              if (isCancelDeal) {
                onCancelTheDeal();
              } else {
                setProcessingContext((prev) => ({
                  ...prev,
                  transferStartDate: contextOfConfirmationModal.nextDate.toISOString(),
                  isChanged: true,
                  type: "transfer",
                }));
              }
              closeConfirmationModal();
              setTimeout(() => {
                isConfirmedRef.current = false;
              });
            },
            isDanger: isCancelDeal,
            loading: isCheckReturnDateLoading,
          },
          decline: {
            label: "Cancel",
            onDecline: declineConfirmationModal,
          },
        }}
        title={
          isCancelDeal
            ? "Cancel Deal"
            : contextOfConfirmationModal.type === "transfer"
            ? "Change Transfer Date"
            : "Change Return Date"
        }
        isOpen={isOpenModalAgreement}
        onClose={() => {
          // ModalComponent закрывает себя после confirm — на переходе ко второму шагу это игнорируем
          if (keepModalOpenRef.current) {
            keepModalOpenRef.current = false;
            return;
          }
          closeConfirmationModal();
          if (isConfirmedRef.current) return;
          if (contextOfConfirmationModal.type === "cancel") return;
          form.setFieldValue("date", {
            transferDate: currentDates.from,
            returnDate: currentDates.to,
          });
        }}
        footer={
          isReturnConfirmStep ? (
            isNextDateInFuture ? (
              <>
                <CustomButton
                  sizeButton="large"
                  typeButton="tertiary"
                  htmlType="button"
                  onClick={declineConfirmationModal}
                >
                  Cancel
                </CustomButton>
                <CustomButton
                  sizeButton="large"
                  typeButton="primary"
                  htmlType="button"
                  onClick={() => applyReturnDate()}
                >
                  Confirm
                </CustomButton>
              </>
            ) : (
              <>
                <CustomButton
                  sizeButton="large"
                  typeButton="critical"
                  htmlType="button"
                  onClick={() => applyReturnDate(true)}
                >
                  With Dispute
                </CustomButton>
                <CustomButton
                  sizeButton="large"
                  typeButton="primary"
                  htmlType="button"
                  onClick={() => applyReturnDate(false)}
                >
                  Without Dispute
                </CustomButton>
              </>
            )
          ) : undefined
        }
        content={
          isReturnConfirmStep ? (
            <div>
              {isNextDateInFuture
                ? `Changing the Return Date will cancel ${cancelRentsCount} upcoming ${
                    cancelRentsCount === 1 ? "Rental" : "Rentals"
                  }. Please ensure the Lessor is informed`
                : "Has the Product been returned with or without Dispute?"}
            </div>
          ) : !isCancelDeal ? (
            <div>
              {contextOfConfirmationModal.type === "transfer" &&
                getDateOnly(contextOfConfirmationModal.nextDate.toDate()) >= getDateOnly(new Date()) && (
                  <div style={{ marginBottom: 24 }}>
                    Please confirm that the new transfer date has been agreed by the users
                  </div>
                )}
              {contextOfConfirmationModal.type === "transfer" &&
                getDateOnly(contextOfConfirmationModal.nextDate.toDate()) < getDateOnly(new Date()) && (
                  <div style={{ marginBottom: 24 }}>
                    Please make sure that the product has been transferred on the indicated date
                  </div>
                )}

              {contextOfConfirmationModal.type === "return" &&
                getDateOnly(contextOfConfirmationModal.nextDate.toDate()) >= getDateOnly(new Date()) && (
                  <div style={{ marginBottom: 24 }}>
                    Please confirm that new return date has been agreed by the users
                  </div>
                )}

              {contextOfConfirmationModal.type === "return" &&
                getDateOnly(contextOfConfirmationModal.nextDate.toDate()) < getDateOnly(new Date()) && (
                  <div style={{ marginBottom: 24 }}>
                    Please make sure that the product has been returned on the indicated date
                  </div>
                )}
              <div>
                {contextOfConfirmationModal?.oldDate?.format("DD.MM.YYYY")} →{" "}
                <span style={{ color: "#F40909" }}>{contextOfConfirmationModal?.nextDate?.format("DD.MM.YYYY")}</span>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ marginBottom: 24 }}>
                Please confirm that the product has not been transferred and the deal is going to be canceled
              </div>
            </div>
          )
        }
      />
      <CompleteDisputeModal
        isOpen={!!completeModalValues}
        onClose={() => setCompleteModalValues(null)}
        onConfirm={confirmCompleteDispute}
        loading={isConfirmCompleteLoading || isPostFileReportLoading || isFinishDebate}
        values={completeModalValues}
        lessorRestriction={!!processingContext.lessorRestriction}
        lesseeRestriction={!!processingContext.lesseeRestriction}
        stopProduct={!!processingContext.stopProduct}
        returnType={processingContext.return}
        insuranceType={insuranceType}
        onOpenReport={onOpenReportDocument}
        isOpenReportLoading={isOpenReportLoading}
      />
      <ModalReport isOpen={isOpenModalReport} onClose={onCloseModalReport} debateId={debateInfo.id} />
    </>
  );
};
